/**
 * queryParser — a typed sentence → a question and a set of filters.
 *
 * ─── WHY THIS IS NOT A MODEL ───
 * Everything this has to recognise is already a CLOSED LIST that the server publishes:
 * `/search/schema` gives the colours, genders, age bands, accessories, vehicle types and
 * directions; the camera and group lists give the place names. Against a fixed vocabulary of a
 * few hundred terms, matching words is not an approximation of understanding — it is the same
 * answer a model would have to arrive at, reached deterministically and offline.
 *
 * So this is the whole feature today, and it is also the SEAM. It returns a parse, not a result;
 * the page applies the parse to its controls; the controls run the search. Swapping this for an
 * `await search.parseQuery(text)` when the neural module lands changes one call site and nothing
 * else, and the operator-visible behaviour — a parse you can see and correct — stays identical.
 *
 * ─── TWO RULES ───
 * 1. NOTHING IS SILENTLY DROPPED. Every word that did not become part of the query comes back in
 *    `unparsed`, and the page shows it. A search bar that ignores half a sentence and returns
 *    confident results is the same failure as an attribute filter that matches nothing: the
 *    answer looks like an answer.
 * 2. NO SILENT CORRECTIONS. Spelling tolerance is real — "red shire person" is a typo, not a
 *    different question — but every fix is reported in `corrections` and rendered, because a
 *    correction the operator cannot see is a query they did not write.
 *
 * The parse can only ever produce values FROM the schema, so it cannot build a query the API
 * would reject.
 */
import type { CameraRef, Group, PersonRef, SearchSchema } from '../../api/contracts';
import type { Filters, QuestionKey, SubjectKey } from './searchModel';

export interface ParseContext {
  schema: SearchSchema | null;
  cameras: CameraRef[];
  groups: Group[];
  /**
   * The registered people, so a name in the sentence can become a filter.
   *
   * A CLOSED LIST, like the camera and group names — and for the same reason. There is no way to
   * tell a name from a typo by looking at it, so an unrecognised word must stay unparsed rather
   * than becoming a person filter that matches nobody and reports it as an answer. Empty (the
   * host cannot list the registry, or the user lacks the permission) simply means no name is
   * recognised; everything else about the parse is unaffected.
   */
  people?: PersonRef[];
  /** The subject in force before parsing. Kept unless the text names another. */
  subject: SubjectKey;
}

export interface ParsedQuery {
  question: QuestionKey;
  /** Only the dimensions the text actually spoke to. Everything else is left as it was. */
  filters: Partial<Filters>;
  /** `[asTyped, readAs]` — every spelling fix, so the page can show it. */
  corrections: Array<[string, string]>;
  /** Words that became no part of the query. Shown, never swallowed. */
  unparsed: string[];
  /** One line per thing the parse decided, for the "Read as" row. */
  notes: string[];
}

// ─── Word lists ────────────────────────────────────────────────────────────

/**
 * Garment words, and which colour field they bind a colour to.
 *
 * This binding is what makes "red shirt" mean `upper_color` rather than "a red something". A
 * bare colour still lands on `upper_color` (see below) because that is overwhelmingly what people
 * mean by "a red person", but the explicit word wins where it is present.
 */
const UPPER_GARMENTS = [
  'shirt', 'tshirt', 't-shirt', 'top', 'tee', 'jacket', 'kurta', 'blouse',
  'coat', 'hoodie', 'sweater', 'jersey', 'uniform', 'vest',
];
const LOWER_GARMENTS = [
  'pant', 'pants', 'trouser', 'trousers', 'jeans', 'skirt', 'shorts', 'lower', 'bottom',
];

const GENDER_WORDS: Record<string, string> = {
  male: 'male', man: 'male', men: 'male', boy: 'male', boys: 'male', gentleman: 'male',
  female: 'female', woman: 'female', women: 'female', girl: 'female', girls: 'female', lady: 'female',
};

/** Words for an age band, alongside the literal band values from the schema. */
const AGE_WORDS: Record<string, string> = {
  child: '0-12', children: '0-12', kid: '0-12', kids: '0-12', infant: '0-12',
  teen: '13-17', teens: '13-17', teenager: '13-17', teenagers: '13-17',
  young: '18-29', youth: '18-29',
  adult: '30-45', adults: '30-45',
  senior: '60+', seniors: '60+', elderly: '60+', old: '60+',
};

/** Synonyms onto the `accessories` vocabulary. The vocabulary values themselves match directly. */
const ACCESSORY_WORDS: Record<string, string> = {
  bag: 'handbag', purse: 'handbag', handbags: 'handbag',
  rucksack: 'backpack', backpacks: 'backpack',
  hardhat: 'helmet', 'hard-hat': 'helmet', helmets: 'helmet', hardhats: 'helmet',
  spectacles: 'glasses', specs: 'glasses', eyeglasses: 'glasses',
  cap: 'hat', caps: 'hat', hats: 'hat',
  luggage: 'suitcase', trolley: 'suitcase', suitcases: 'suitcase',
  masks: 'mask', facemask: 'mask',
  umbrellas: 'umbrella',
};

const DIRECTION_WORDS: Record<string, string> = {
  entering: 'entry', entry: 'entry', in: 'entry', inbound: 'entry', arriving: 'entry',
  exiting: 'exit', exit: 'exit', out: 'exit', outbound: 'exit', leaving: 'exit',
};

const PERSON_SUBJECT_WORDS = ['person', 'people', 'persons', 'someone', 'anyone', 'employee', 'employees', 'staff'];
const VEHICLE_SUBJECT_WORDS = ['vehicle', 'vehicles', 'car', 'cars', 'bike', 'bikes', 'truck', 'trucks', 'motorcycle', 'van'];

const KNOWN_WORDS = ['employee', 'employees', 'staff', 'known', 'enrolled', 'registered', 'identified'];

/**
 * The watch list, in the words an operator uses for it.
 *
 * Each maps to a CATEGORY where the word names one — "wanted" and "stolen" are listings, not
 * moods — and to none where it is the general question. "Blacklisted" is deliberately the
 * general reading as well as its own category: it is what most people call the whole list, and
 * narrowing their query to one rung of it would quietly drop the others.
 */
const WATCHLIST_WORDS: Array<[string[], string | null]> = [
  [['blacklisted', 'blacklist', 'flagged', 'watchlisted', 'watchlist'], null],
  [['wanted'], 'wanted'],
  [['missing'], 'missing'],
  [['stolen'], 'stolen'],
];
const NOT_WATCHLIST_WORDS = ['unflagged', 'unlisted'];
const UNKNOWN_WORDS = ['unknown', 'stranger', 'strangers', 'visitor', 'visitors', 'unidentified', 'unrecognised', 'unrecognized'];

const TIME_WORDS: Array<[string[], string]> = [
  [['today'], 'today'],
  [['yesterday'], 'yesterday'],
  [['last', '7', 'days'], 'last_7_days'],
  [['last', 'seven', 'days'], 'last_7_days'],
  [['last', 'week'], 'last_7_days'],
  [['past', 'week'], 'last_7_days'],
  [['last', '30', 'days'], 'last_30_days'],
  [['last', 'thirty', 'days'], 'last_30_days'],
  [['last', 'month'], 'last_month'],
  [['this', 'month'], 'this_month'],
];

/**
 * Question phrases, LONGEST FIRST so "which areas" is not eaten by "which".
 *
 * `verdict` has no phrase here at all, because it cannot be decided from one: "is the red shirt
 * person in the server room" and "is anyone wearing a helmet" both open with "is", and only the
 * first names a place to be a yes or no about. It is resolved at the end, from the opening word
 * plus whether a place was found — see step 8.
 */
const QUESTION_PHRASES: Array<[string[], QuestionKey]> = [
  [['entry', 'to', 'exit'], 'journey'],
  [['which', 'areas'], 'where'],
  [['which', 'area'], 'where'],
  [['which', 'places'], 'where'],
  [['which', 'place'], 'where'],
  [['which', 'rooms'], 'where'],
  [['which', 'room'], 'where'],
  [['which', 'cameras'], 'where'],
  [['what', 'time'], 'when'],
  [['at', 'what', 'time'], 'when'],
  [['how', 'many', 'wore'], 'compliance'],
  // "time line" as two words is how people type it, and it must not be read as the word "time".
  [['time', 'line'], 'journey'],
  [['timeline'], 'journey'],
  [['journey'], 'journey'],
  [['trail'], 'journey'],
  [['route'], 'journey'],
  [['movement'], 'journey'],
  [['movements'], 'journey'],
  [['compliance'], 'compliance'],
  [['where'], 'where'],
  [['when'], 'when'],
];

/**
 * Words that carry no filter and must not be reported as unparsed.
 *
 * Deliberately generous. `unparsed` exists to surface a word the operator expected to matter —
 * a colour that is not collected, a room that is not a camera — and burying that under "the",
 * "did" and "wearing" would make the warning worthless.
 */
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'of', 'is', 'was', 'are', 'were', 'am', 'be', 'been', 'being',
  'did', 'do', 'does', 'done', 'has', 'have', 'had',
  'and', 'or', 'not', 'but', 'with', 'without', 'for', 'by', 'during', 'near', 'inside',
  'in', 'at', 'on', 'to', 'from', 'into', 'onto', 'over', 'across', 'through', 'between',
  'who', 'whom', 'whose', 'what', 'which', 'that', 'this', 'these', 'those', 'there',
  'all', 'any', 'every', 'everyone', 'everybody', 'somebody', 'each', 'both',
  'wear', 'wears', 'wearing', 'wore', 'worn', 'dressed',
  'visit', 'visits', 'visited', 'visiting', 'went', 'go', 'goes', 'gone', 'came', 'come',
  'enter', 'entered', 'exited', 'left', 'walked', 'moved',
  'see', 'saw', 'seen', 'show', 'shows', 'showed', 'find', 'found', 'list', 'get',
  'me', 'my', 'us', 'i', 'you', 'it', 'they', 'them', 'their', 'his', 'her',
  'area', 'areas', 'place', 'places', 'room', 'rooms', 'zone', 'zones', 'site', 'sites',
  'time', 'times', 'when', 'where', 'how', 'many', 'much', 'ago',
  'colour', 'color', 'coloured', 'colored', 'shirt', 'shirts',
  'carrying', 'carried', 'holding', 'wearing',
]);

// ─── Fuzzy matching ────────────────────────────────────────────────────────

/**
 * Is `a` reachable from `b` in one edit?
 *
 * Bounded at one on purpose. One edit covers the typos people actually make — a transposition, a
 * dropped letter, "shire" for "shirt" — while two starts turning one vocabulary term into
 * another: at distance two "black" reaches "blue" and "red" reaches "bed". A tolerance that
 * changes which colour was searched for is worse than no tolerance at all.
 *
 * Written as an early-exit single-edit check rather than a full Levenshtein matrix because that
 * is all that is ever asked of it.
 */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  const dl = a.length - b.length;
  if (dl > 1 || dl < -1) return false;

  if (dl === 0) {
    // One substitution — or one transposition of adjacent letters.
    let diff = -1;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        if (diff >= 0) {
          return diff === i - 1 && a[diff] === b[i] && a[i] === b[diff];
        }
        diff = i;
      }
    }
    return diff >= 0;
  }

  // One insertion / deletion: walk both, allowing a single skip in the longer.
  const [long, short] = dl === 1 ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < long.length && j < short.length) {
    if (long[i] === short[j]) { i += 1; j += 1; continue; }
    if (skipped) return false;
    skipped = true;
    i += 1;
  }
  return true;
}

/** Tokens shorter than this are matched exactly — 'red', 'hat' and 'car' have no slack to give. */
const FUZZY_MIN_LENGTH = 4;

// ─── Token tape ────────────────────────────────────────────────────────────

interface Tok { word: string; used: boolean }

/**
 * The sentence as a tape of words, consumed left to right by each matcher in turn.
 *
 * Consuming rather than scanning is what keeps one word from feeding two filters: once "red" has
 * become an upper colour it is gone, so it cannot also become a vehicle colour, and "server room"
 * taken as a place cannot leave "room" behind to be reported as unparsed.
 */
class Tape {
  toks: Tok[];

  corrections: Array<[string, string]> = [];

  constructor(text: string) {
    this.toks = text
      .toLowerCase()
      // Hyphens survive ('t-shirt', 'hard-hat'); '+' survives for the '60+' age band.
      .replace(/[^a-z0-9\s+-]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => ({ word, used: false }));
  }

  /**
   * Find an unused run matching `phrase`, and consume it.
   *
   * `fuzzy` is applied per word, so "last seven dayz" still resolves. Every fix that is not an
   * exact match is recorded, because the operator has to be able to see it.
   */
  take(phrase: string[], opts: { fuzzy?: boolean } = {}): boolean {
    return this.takeAt(phrase, opts) >= 0;
  }

  /**
   * `take`, reporting WHERE it matched — the index of the first token, or -1.
   *
   * Position matters to the one matcher that can legitimately hit twice in a sentence: two
   * registered names. Candidates are tried longest-first, so without the index "asha or rahul
   * nair" would prefer Rahul — the longer name rather than the one the operator put first.
   */
  takeAt(phrase: string[], { fuzzy = true }: { fuzzy?: boolean } = {}): number {
    outer:
    for (let i = 0; i + phrase.length <= this.toks.length; i += 1) {
      const fixes: Array<[string, string]> = [];
      for (let k = 0; k < phrase.length; k += 1) {
        const tok = this.toks[i + k];
        if (tok.used) continue outer;
        if (tok.word === phrase[k]) continue;
        const canFuzz = fuzzy && tok.word.length >= FUZZY_MIN_LENGTH && phrase[k].length >= FUZZY_MIN_LENGTH;
        if (!canFuzz || !withinOneEdit(tok.word, phrase[k])) continue outer;
        fixes.push([tok.word, phrase[k]]);
      }
      for (let k = 0; k < phrase.length; k += 1) this.toks[i + k].used = true;
      this.corrections.push(...fixes);
      return i;
    }
    return -1;
  }

  /** The index of an unused token matching `word`, or -1. Does NOT consume. */
  find(word: string, fuzzy = true): number {
    for (let i = 0; i < this.toks.length; i += 1) {
      const tok = this.toks[i];
      if (tok.used) continue;
      if (tok.word === word) return i;
      const canFuzz = fuzzy && tok.word.length >= FUZZY_MIN_LENGTH && word.length >= FUZZY_MIN_LENGTH;
      if (canFuzz && withinOneEdit(tok.word, word)) return i;
    }
    return -1;
  }

  consume(index: number, readAs?: string): string {
    const tok = this.toks[index];
    tok.used = true;
    if (readAs && tok.word !== readAs) this.corrections.push([tok.word, readAs]);
    return tok.word;
  }

  /** The first unused token index whose word is in `words`, with the matched word. */
  findAny(words: string[]): { index: number; word: string } | null {
    for (const w of words) {
      const i = this.find(w);
      if (i >= 0) return { index: i, word: w };
    }
    return null;
  }

  leftovers(): string[] {
    return this.toks
      .filter((t) => !t.used && !STOP_WORDS.has(t.word) && !/^\d+$/.test(t.word))
      .map((t) => t.word);
  }
}

// ─── Places ────────────────────────────────────────────────────────────────

interface PlaceCandidate {
  kind: 'camera' | 'group';
  id: string;
  /** The name, tokenised — matched longest-first so "Server Room" beats a camera called "Room". */
  words: string[];
}

/**
 * Camera and group names as match candidates, longest name first.
 *
 * GROUPS BEFORE CAMERAS at equal length, because a group is what an operator means by a place:
 * "office" is an area containing cameras, and binding it to a single camera that happens to be
 * called "Office" would narrow the search to one view of the room.
 */
function placeCandidates(cameras: CameraRef[], groups: Group[]): PlaceCandidate[] {
  const tokenise = (name: string) => name.toLowerCase().replace(/[^a-z0-9\s+-]/g, ' ').split(/\s+/).filter(Boolean);

  const out: PlaceCandidate[] = [
    ...groups.map((g) => ({ kind: 'group' as const, id: g.id, words: tokenise(g.name || '') })),
    ...cameras.map((c) => ({ kind: 'camera' as const, id: c.id, words: tokenise(c.name || '') })),
  ].filter((p) => p.words.length > 0);

  return out.sort((a, b) => (
    b.words.length - a.words.length
    || (a.kind === b.kind ? 0 : a.kind === 'group' ? -1 : 1)
  ));
}

// ─── People ────────────────────────────────────────────────────────────────

interface NameCandidate {
  personId: string;
  /** What to put in the filter — the registry's own spelling, not the operator's. */
  name: string;
  words: string[];
}

/**
 * Every word this parser already gives a meaning to.
 *
 * A name that IS one of these is not usable as a name: a person called "Red" or "Van" would
 * otherwise swallow the colour out of "red shirt" and turn a vehicle search into a search for
 * one employee. The vocabulary reading is the one the operator meant in nine sentences out of
 * ten, and the Name box is still there for the tenth — so the collision is resolved in favour of
 * the vocabulary, silently, because the alternative is a query nobody can explain.
 *
 * Only whole-name collisions are dropped. "Rose Mathew" keeps working next to the colour "rose",
 * because two words that must appear together cannot be a stray adjective.
 */
function reservedWords(vocab: Record<string, string[]>): Set<string> {
  const out = new Set<string>([
    ...STOP_WORDS,
    ...UPPER_GARMENTS, ...LOWER_GARMENTS,
    ...Object.keys(GENDER_WORDS), ...Object.keys(AGE_WORDS),
    ...Object.keys(ACCESSORY_WORDS), ...Object.keys(DIRECTION_WORDS),
    ...PERSON_SUBJECT_WORDS, ...VEHICLE_SUBJECT_WORDS,
    ...KNOWN_WORDS, ...UNKNOWN_WORDS,
  ]);
  for (const values of Object.values(vocab || {})) for (const v of values) out.add(v);
  for (const [phrase] of TIME_WORDS) for (const w of phrase) out.add(w);
  for (const [phrase] of QUESTION_PHRASES) for (const w of phrase) out.add(w);
  return out;
}

/**
 * Registered names as match candidates, longest first so "Asha Verma" beats "Asha".
 *
 * The registry can be large and is matched in memory on every parse, so this is built once per
 * parse from a list the page already holds.
 */
function nameCandidates(people: PersonRef[], reserved: Set<string>): NameCandidate[] {
  const out: NameCandidate[] = [];
  for (const p of people) {
    const name = (p.name || '').trim();
    if (!name) continue;
    const words = name.toLowerCase().replace(/[^a-z0-9\s+-]/g, ' ').split(/\s+/).filter(Boolean);
    if (!words.length) continue;
    // A single-word name that collides with the vocabulary is not offered — see reservedWords.
    if (words.length === 1 && reserved.has(words[0])) continue;
    out.push({ personId: p.person_id, name, words });
  }
  return out.sort((a, b) => b.words.length - a.words.length);
}

/**
 * A registered first name the parser would actually recognise on its own, or null.
 *
 * For the query bar's example row. The constraint is not cosmetic: a name is only typeable when
 * it survives `nameCandidates`, so offering "where was Rose today" for somebody called Rose
 * would hand the operator a button that parses their colleague as a colour and returns the
 * wrong answer with no sign anything went wrong.
 */
export function firstTypeableName(people: PersonRef[], schema: SearchSchema | null): string | null {
  const reserved = reservedWords(schema?.attributes || {});
  for (const person of people) {
    const first = (person.name || '').trim().split(/\s+/)[0] || '';
    const word = first.toLowerCase();
    // Three letters minimum: shorter than that and the word is more likely an initial than a
    // name, and it reads as noise in an example sentence.
    if (/^[a-z]{3,}$/.test(word) && !reserved.has(word)) return first;
  }
  return null;
}

// ─── The parse ─────────────────────────────────────────────────────────────

export function parseQuery(text: string, ctx: ParseContext): ParsedQuery {
  const tape = new Tape(text);
  const filters: Partial<Filters> = {};
  const notes: string[] = [];

  const vocab = ctx.schema?.attributes || {};
  /**
   * 'unknown' is dropped from every vocabulary the free-text matcher sees.
   *
   * It is a real stored value — "we saw a person and could not read their shirt" — and the
   * controls still offer it. But as a typed word it always means an unidentified PERSON, which
   * the identity matcher claims, and letting it also match a colour would turn "unknown person in
   * the office" into a search for people whose shirt colour went unrecorded.
   */
  const searchable = (values: string[] | undefined) => (values || []).filter((v) => v !== 'unknown');
  const colours = searchable(vocab.upper_color);
  const accessories = searchable(vocab.accessories);
  const vehicleTypes = searchable(vocab.vehicle_type);
  const ageBands = searchable(vocab.age_band);

  // ── 1. The question. Resolved FIRST because it changes how later words are read: under
  //       "did all of them…?" a helmet is the thing being measured, not a filter narrowing the
  //       set — see the split below.
  //
  //    EVERY question phrase is consumed, not just the winning one. "timeline of person from
  //    entry to exit" states the same question twice; if only the first match were taken, the
  //    other phrase would be left on the tape and reported as unparsed — which reads to the
  //    operator as "I did not understand half your sentence" about the half it understood best.
  let question: QuestionKey | null = null;
  const alsoStated: QuestionKey[] = [];
  for (const [phrase, key] of QUESTION_PHRASES) {
    while (tape.take(phrase)) {
      if (question === null) question = key;
      else if (key !== question && !alsoStated.includes(key)) alsoStated.push(key);
    }
  }

  // "all … wear/wearing" — a compliance question even without the word "compliance".
  if (!question) {
    const hasAll = tape.toks.some((t) => ['all', 'every', 'everyone', 'everybody'].includes(t.word));
    const hasWear = tape.toks.some((t) => ['wear', 'wears', 'wearing', 'wore', 'worn', 'have', 'has'].includes(t.word));
    if (hasAll && hasWear) question = 'compliance';
  }

  // A leading is/was/did is a YES-OR-NO question — but only if it turns out to name a place.
  const opensWithQuestion = ['is', 'was', 'did', 'has', 'have']
    .includes(tape.toks[0]?.word || '');

  // ── 2. Time.
  for (const [phrase, preset] of TIME_WORDS) {
    if (tape.take(phrase)) { filters.time = { preset }; break; }
  }

  // ── 3. Places, before attributes: a camera called "Red Gate" must be read as a place rather
  //       than stripped for its colour.
  const cameras: string[] = [];
  const groups: string[] = [];
  for (const cand of placeCandidates(ctx.cameras, ctx.groups)) {
    if (!tape.take(cand.words)) continue;
    if (cand.kind === 'group') groups.push(cand.id);
    else cameras.push(cand.id);
  }
  if (groups.length) filters.groups = groups;
  if (cameras.length) filters.cameras = cameras;
  const foundPlace = groups.length > 0 || cameras.length > 0;

  // ── 3b. A registered NAME — "where was asha today".
  //
  //    AFTER places, because a camera named after somebody is still a place: "reception" in a
  //    sentence means the desk, not the person on it, and the place list is the one an operator
  //    types deliberately. BEFORE attributes and the subject, so a name can claim its own words
  //    before the colour matcher goes looking for loose adjectives.
  //
  //    MATCHED EXACTLY — `fuzzy: false`, the only matcher here that refuses spelling tolerance.
  //    One edit turns Asha into Usha: a different human being, searched for under a correction
  //    the operator half-reads and accepts. Every other vocabulary in this file is a category
  //    where a near miss is the same question; a name is not.
  //
  //    Candidates are tried LONGEST first, so "Asha Verma" beats "Asha", but the winner is then
  //    the one typed EARLIEST — "asha or rahul nair" is a question about Asha.
  const namesMatched: Array<NameCandidate & { at: number }> = [];
  for (const cand of nameCandidates(ctx.people || [], reservedWords(vocab))) {
    const at = tape.takeAt(cand.words, { fuzzy: false });
    if (at >= 0) namesMatched.push({ ...cand, at });
  }
  namesMatched.sort((a, b) => a.at - b.at);
  if (namesMatched.length) {
    // One name per query: `person.name` is a single prefix filter server-side. The others are
    // named in a note rather than dropped in silence.
    filters.personName = namesMatched[0].name;
    // The subject itself is settled in step 4, which reads `namesMatched`: a name is a person
    // whatever tab was open, and only the person subject sends `person.name` at all, so leaving
    // a Vehicle tab selected would drop the filter the operator just typed.
    notes.push(`Looking for ${namesMatched[0].name}`);
    if (namesMatched.length > 1) {
      notes.push(`Ignoring ${namesMatched.slice(1).map((n) => n.name).join(', ')} — one name at a time`);
    }
  }

  // ── 4. Subject. A vehicle word switches the tab; a person word confirms it.
  const vehicleHit = tape.findAny(VEHICLE_SUBJECT_WORDS);
  const personHit = tape.findAny(PERSON_SUBJECT_WORDS);
  let subject: SubjectKey = ctx.subject;
  // A named person outranks a vehicle word: "asha near the truck" is about Asha.
  if (vehicleHit && !personHit && !namesMatched.length) {
    subject = 'vehicle';
    // NOT consumed when it is also a vehicle TYPE — "car" is both the subject and the type, and
    // the type filter below is the more specific reading.
    if (!vehicleTypes.includes(vehicleHit.word)) tape.consume(vehicleHit.index);
  } else if (personHit || namesMatched.length) {
    subject = 'person';
    // Left for the identity matcher when it is one of ITS words: "employee" says both "a person"
    // and "someone enrolled", and only the second is a filter.
    if (personHit && !KNOWN_WORDS.includes(personHit.word)) tape.consume(personHit.index);
  }
  if (subject !== ctx.subject) filters.subject = subject;

  // ── 5. Identity, BEFORE attributes — because "unknown" is a word both of them want.
  //
  //    'unknown' is a legitimate value of upper_color, gender and age_band: it is what the
  //    projection stores when a model saw a person but could not read their shirt. But nobody
  //    types "unknown person" to mean "someone whose shirt colour was not recorded" — they mean
  //    an unidentified person. Matched here first, and excluded from the vocabulary matching
  //    below, so the word means what the operator meant. The controls can still select the
  //    attribute value; only the free-text reading of the bare word is claimed.
  const knownHit = tape.findAny(KNOWN_WORDS);
  const unknownHit = tape.findAny(UNKNOWN_WORDS);
  if (unknownHit) { tape.consume(unknownHit.index, unknownHit.word); filters.known = false; }
  else if (knownHit) { tape.consume(knownHit.index, knownHit.word); filters.known = true; }

  /**
   * ── 5b. The watch list ──
   *
   * After identity and before attributes, for the same reason identity comes first: "missing" is
   * a watch-list category AND a word that could be read as an attribute value, and the operator
   * who types it means the list. Claimed here, once, and the word is consumed so nothing further
   * down can read it as something else.
   */
  const unflaggedHit = tape.findAny(NOT_WATCHLIST_WORDS);
  if (unflaggedHit) {
    tape.consume(unflaggedHit.index, unflaggedHit.word);
    filters.watchlist = 'not_listed';
  } else {
    const categories: string[] = [];
    let listed = false;
    for (const [words, category] of WATCHLIST_WORDS) {
      const hit = tape.findAny(words);
      if (!hit) continue;
      tape.consume(hit.index, hit.word);
      listed = true;
      if (category) categories.push(category);
    }
    if (listed) {
      filters.watchlist = 'listed';
      // No category named means the whole list — which is what "show me the flagged ones" asks.
      if (categories.length) filters.watchlistCategories = categories;
    }
  }

  // ── 6. Attributes.
  const attributes: Record<string, string[]> = {};
  const addAttr = (field: string, value: string) => {
    if (!attributes[field]) attributes[field] = [];
    if (!attributes[field].includes(value)) attributes[field].push(value);
  };

  /** A colour bound to a garment: "red shirt", and "shirt red" for the way people sometimes type. */
  const takeColourGarment = (garments: string[], field: string) => {
    for (const garment of garments) {
      for (let guard = 0; guard < 4; guard += 1) {
        const gi = tape.find(garment);
        if (gi < 0) break;
        // Look either side for an unused colour word.
        let hit: { index: number; colour: string } | null = null;
        for (const offset of [-1, 1, -2]) {
          const ci = gi + offset;
          const tok = tape.toks[ci];
          if (!tok || tok.used) continue;
          const colour = colours.find((c) => tok.word === c
            || (tok.word.length >= FUZZY_MIN_LENGTH && c.length >= FUZZY_MIN_LENGTH && withinOneEdit(tok.word, c)));
          if (colour) { hit = { index: ci, colour }; break; }
        }
        if (!hit) break;
        tape.consume(gi, garment);
        tape.consume(hit.index, hit.colour);
        addAttr(field, hit.colour);
      }
    }
  };

  takeColourGarment(UPPER_GARMENTS, 'upper_color');
  takeColourGarment(LOWER_GARMENTS, 'lower_color');

  // Gender, age, accessories, vehicle type/direction — each may appear more than once
  // ("red or blue shirt"), so every matcher loops until the tape stops yielding.
  const takeMapped = (words: Record<string, string>, field: string) => {
    for (const [word, value] of Object.entries(words)) {
      for (let guard = 0; guard < 4; guard += 1) {
        const i = tape.find(word);
        if (i < 0) break;
        tape.consume(i, word);
        addAttr(field, value);
      }
    }
  };
  const takeVocab = (values: string[], field: string) => {
    for (const value of values) {
      for (let guard = 0; guard < 4; guard += 1) {
        const i = tape.find(value);
        if (i < 0) break;
        tape.consume(i, value);
        addAttr(field, value);
      }
    }
  };

  takeMapped(GENDER_WORDS, 'gender');
  takeVocab(ageBands, 'age_band');
  takeMapped(AGE_WORDS, 'age_band');
  takeVocab(accessories, 'accessories');
  takeMapped(ACCESSORY_WORDS, 'accessories');

  if (subject === 'vehicle') {
    takeVocab(vehicleTypes, 'vehicle_type');
    takeMapped(DIRECTION_WORDS, 'direction');
    takeVocab(searchable(vocab.vehicle_color), 'vehicle_color');
  }

  // A bare colour, last: everything with a stronger claim on it has already had its chance.
  // Bound to the UPPER garment because that is what "a red person" means to everyone who says it.
  {
    const colourField = subject === 'vehicle' ? 'vehicle_color' : 'upper_color';
    if (!attributes[colourField]) takeVocab(colours, colourField);
  }

  // ── 7. A plate, for the vehicle subject: two letters then three or more alphanumerics.
  if (subject === 'vehicle') {
    const i = tape.toks.findIndex((t) => !t.used && /^[a-z]{2}[a-z0-9]{3,}$/.test(t.word) && !/^[a-z]+$/.test(t.word));
    if (i >= 0) filters.plate = tape.consume(i).toUpperCase();
  }

  // ── 8. Resolve the question, now that the whole sentence has been read.
  if (!question && opensWithQuestion) {
    /**
     * A yes/no question. It needs somewhere to be a yes or no ABOUT.
     *
     * "is the red shirt person in the server room" names one, and becomes a verdict. "is anyone
     * wearing a helmet" does not, and a Yes/No panel spanning the whole estate would answer a
     * question nobody asked. So that degrades to the plain list — which genuinely answers it,
     * since the count IS the yes or no, and it shows the evidence — and the note names the one
     * thing missing rather than leaving the operator to wonder why they got a list.
     */
    if (foundPlace) {
      question = 'verdict';
    } else {
      question = 'list';
      notes.push('Read as a yes/no — add a camera or area and it becomes a Yes/No answer');
    }
  }
  if (!question) question = 'list';

  if (alsoStated.length) {
    notes.push(`That sentence asks more than one thing; answering "${question}" first`);
  }

  // ── 9. Under a compliance question, the thing being measured is the SPLIT, not a filter.
  //
  //    "is all employee wear helmet" filtered to helmet-wearers would ask "of the people wearing
  //    a helmet, how many wore a helmet" — 100%, always. The server rejects that outright; here
  //    the accessory is simply moved to where it belongs.
  if (question === 'compliance') {
    const field = attributes.accessories?.length ? 'accessories'
      : attributes.upper_color?.length ? 'upper_color'
        : null;
    if (field) {
      const value = attributes[field][0];
      filters.splitBy = { field, value };
      delete attributes[field];
      notes.push(`Measuring how many people had ${value}, rather than filtering to them`);
    }
  }

  if (Object.keys(attributes).length) filters.attributes = attributes;
  filters.question = question;

  return {
    question,
    filters,
    // De-duplicated: "red shirt red pants" would otherwise report the same fix twice.
    corrections: [...new Map(tape.corrections.map((c) => [c.join('→'), c])).values()],
    unparsed: [...new Set(tape.leftovers())],
    notes,
  };
}

/**
 * The starter examples, shown under an empty bar.
 *
 * They are the syntax documentation. Nobody reads a help panel about a search box, and every one
 * of these exercises a different answer shape — which is the thing an operator has to discover
 * exists at all.
 */
export const QUERY_EXAMPLES = [
  'red shirt person visited which areas',
  'blue shirt person visit office when',
  'is red shirt person visited server room',
  // Was "timeline of person from entry to exit", which reads as a JOURNEY — an answer this
  // page no longer gives, so the example taught the operator that the box does not work.
  // It now lives in TRACK_EXAMPLES, where it is answered.
  'red shirt person with backpack today',
  'is all employee wear helmet',
];

/**
 * The same five shapes, asked of vehicles.
 *
 * Not a translation of the person set — the questions worth teaching differ. Nobody asks
 * whether all vehicles complied with anything, and everybody asks about direction, which has no
 * person equivalent. Every one still has to name a VEHICLE word, or the sentence parses as a
 * person query and the page it was offered on tells the operator it belongs elsewhere.
 */
export const VEHICLE_QUERY_EXAMPLES = [
  'white truck today',
  'red car visited which areas',
  'black motorcycle at gate 1 when',
  'is red car visited main building',
];

/**
 * The same teaching device, for the Tracking page.
 *
 * Every example has to reach an answer shape that page OWNS, or the examples teach the
 * operator that the box does not work: a "which areas" example on Tracking parses fine, gets
 * answered as a trail, and earns a notice explaining why — which is a strange first thing to
 * happen to somebody pressing the button the page offered them.
 *
 * Both starting points are shown on purpose. A trail usually begins with an identity, but it
 * does not have to: "where did the red shirt person go" is the same question asked of an
 * appearance, and it is the one operators do not know they can ask.
 */
export const TRACK_EXAMPLES = [
  // Every one of these has to hit a word QUESTION_PHRASES maps to `journey` — trail, route,
  // journey, timeline, "entry to exit". "where did X go" reads as WHICH AREAS, which is the
  // other page's answer, and was the first thing tried here.
  'trail of the red shirt person today',
  'journey of person from entry to exit',
  // "route of the black motorcycle today" was here, and moved to VEHICLE_TRACK_EXAMPLES when
  // the subject became the route: on Person Tracking it parses as a vehicle and earns the
  // "that reads as a vehicle" notice — the page rejecting its own suggestion.
  'timeline of the blue shirt person today',
];

/** Trails, for the vehicle page. Same rule: a journey word AND a vehicle word, in every one. */
export const VEHICLE_TRACK_EXAMPLES = [
  'route of the black motorcycle today',
  'trail of the white truck today',
  'journey of red car from entry to exit',
];

/**
 * The All page: words, not appearance. Every one is answered by the TEXT search — a name nobody
 * registered, a plate, a misread plate, a word on a board — because that is the one thing this
 * page does that the other two cannot.
 */
export const ALL_QUERY_EXAMPLES = [
  'badal today',
  'MH01HR4321',
  'dethali char rasta at gate 1',
  'vadal yesterday',
];

/** Trails on the All page: a journey word plus something to follow — a name or a plate. */
export const ALL_TRACK_EXAMPLES = [
  'trail of MH01HR4321 today',
  'journey of badal today',
];

/**
 * Ready-made searches, shown as labelled rows under the query bar on the Search side:
 * number plates and words the OCR model reads in this deployment's footage. One press runs the
 * text search, which also finds close misreads.
 */
export const NUMBER_SUGGESTIONS = ['GJ05JF5120', 'GJ03LG1357', 'GJ11AS5023'];
export const TEXT_SUGGESTIONS = ['govt', 'gujarat', 'Patel', 'GSRTC', 'MAHASAGAR'];

export interface SuggestionGroup {
  label: string;
  items: string[];
}

/**
 * The labelled suggestion rows for a page, or null to fall back to the plain examples.
 * Search mode only — Track keeps its "trail of …" sentences, because a bare word pressed there
 * answers as a list, not a trail. Numbers on Vehicle (a plate is a vehicle's identity); numbers
 * and text on All, where the text search covers both.
 */
export function suggestionGroupsFor(mode: 'search' | 'tracking', subject?: SubjectKey): SuggestionGroup[] | null {
  if (mode !== 'search') return null;
  if (subject === 'all') {
    return [{ label: 'Number', items: NUMBER_SUGGESTIONS }, { label: 'Text', items: TEXT_SUGGESTIONS }];
  }
  if (subject === 'vehicle') return [{ label: 'Number', items: NUMBER_SUGGESTIONS }];
  return null;
}

/**
 * The examples for one of the pages.
 *
 * Keyed on both axes because both can make an example bounce, and a bounced example is worse
 * than no example at all: the operator pressed a button the page itself offered and was told
 * the page cannot answer it. The unlocked mount keeps the person sets — it has a subject
 * switch, so a vehicle sentence there is a legal thing to type.
 */
export function examplesFor(mode: 'search' | 'tracking', subject?: SubjectKey): string[] {
  if (subject === 'all') return mode === 'tracking' ? ALL_TRACK_EXAMPLES : ALL_QUERY_EXAMPLES;
  if (mode === 'tracking') return subject === 'vehicle' ? VEHICLE_TRACK_EXAMPLES : TRACK_EXAMPLES;
  return subject === 'vehicle' ? VEHICLE_QUERY_EXAMPLES : QUERY_EXAMPLES;
}

/**
 * The one example built from THIS estate's own data — a registered name.
 *
 * Worded per page for the same reason the lists are: "where was Asha today" reads as WHICH
 * AREAS, which Tracking does not answer, so the personalised example was the one suggestion on
 * that page guaranteed to bounce. Vehicles get none — a plate is not in the people registry,
 * and inventing a plausible-looking one teaches an operator to search for a vehicle that does
 * not exist.
 */
export function nameExampleFor(
  mode: 'search' | 'tracking',
  subject: SubjectKey | undefined,
  name?: string | null,
): string | null {
  if (!name || subject === 'vehicle') return null;
  const first = name.toLowerCase();
  return mode === 'tracking' ? `trail of ${first} today` : `where was ${first} today`;
}
